import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Stack,
  Portal,
  AlertColor,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

export interface Toast {
  id: string;
  message: string;
  type: AlertColor;
  title?: string;
  duration?: number;
  persistent?: boolean;
  action?: ReactNode;
  onClose?: () => void;
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => string;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
  // Convenience methods
  success: (message: string, options?: Partial<Toast>) => string;
  error: (message: string, options?: Partial<Toast>) => string;
  warning: (message: string, options?: Partial<Toast>) => string;
  info: (message: string, options?: Partial<Toast>) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
  defaultDuration?: number;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

// Using default Slide transition

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5,
  defaultDuration = 6000,
  position = { vertical: 'bottom', horizontal: 'right' },
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = useCallback(() => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback((toastData: Omit<Toast, 'id'>): string => {
    const id = generateId();
    const toast: Toast = {
      id,
      duration: defaultDuration,
      ...toastData,
    };

    setToasts(prev => {
      // Remove oldest toasts if we exceed max
      const newToasts = prev.length >= maxToasts ? prev.slice(1) : prev;
      return [...newToasts, toast];
    });

    // Auto-hide non-persistent toasts
    if (!toast.persistent && toast.duration && toast.duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, toast.duration);
    }

    return id;
  }, [defaultDuration, generateId, maxToasts]);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message: string, options?: Partial<Toast>) => {
    return showToast({
      message,
      type: 'success',
      title: 'Success',
      ...options,
    });
  }, [showToast]);

  const error = useCallback((message: string, options?: Partial<Toast>) => {
    return showToast({
      message,
      type: 'error',
      title: 'Error',
      persistent: true, // Errors should be persistent by default
      ...options,
    });
  }, [showToast]);

  const warning = useCallback((message: string, options?: Partial<Toast>) => {
    return showToast({
      message,
      type: 'warning',
      title: 'Warning',
      duration: 8000, // Warnings should stay longer
      ...options,
    });
  }, [showToast]);

  const info = useCallback((message: string, options?: Partial<Toast>) => {
    return showToast({
      message,
      type: 'info',
      title: 'Info',
      ...options,
    });
  }, [showToast]);

  const getIcon = (type: AlertColor) => {
    switch (type) {
      case 'success': return <SuccessIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  const contextValue: ToastContextType = {
    showToast,
    hideToast,
    clearAllToasts,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Toast Container */}
      <Portal>
        <Stack
          spacing={1}
          sx={{
            position: 'fixed',
            zIndex: 9999,
            [position.vertical]: 24,
            [position.horizontal]: 24,
            maxWidth: 400,
            width: '100%',
            pointerEvents: 'none',
          }}
        >
          {toasts.map((toast) => (
            <Snackbar
              key={toast.id}
              open={true}
              sx={{ 
                position: 'relative',
                pointerEvents: 'auto',
              }}
            >
              <Alert
                severity={toast.type}
                icon={getIcon(toast.type)}
                action={
                  <Stack direction="row" spacing={1} alignItems="center">
                    {toast.action}
                    <IconButton
                      size="small"
                      aria-label="close"
                      color="inherit"
                      onClick={() => {
                        hideToast(toast.id);
                        toast.onClose?.();
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                }
                sx={{
                  width: '100%',
                  boxShadow: 3,
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                {toast.title && (
                  <AlertTitle>{toast.title}</AlertTitle>
                )}
                {toast.message}
              </Alert>
            </Snackbar>
          ))}
        </Stack>
      </Portal>
    </ToastContext.Provider>
  );
};